import { ExternalLink } from 'lucide-react'

import type { FooterLinkItem, FooterProps } from './types'

function FooterLink({ text, href, isExternal = false }: FooterLinkItem) {
	return (
		<a
			href={href}
			target={isExternal ? '_blank' : undefined}
			rel={isExternal ? 'noopener noreferrer' : undefined}
			className="inline-flex items-center gap-1 border-b border-brand-800 pt-2 text-lg leading-normal text-pure-white transition-colors hover:border-pure-white"
		>
			{text}
			{isExternal && <ExternalLink className="size-3.5 shrink-0" aria-hidden="true" />}
		</a>
	)
}

export default function Footer({
	logo = { src: '', url: '/', alt: '' },
	address = '',
	email = '',
	phone = '',
	linkColumns = [],
	legalLinks = [],
}: FooterProps) {
	// Figma renders the address as 2 lines (street / postal+city); the prop carries
	// a single comma-separated string, so split on the first comma to reproduce it.
	const [addressLine1, addressLine2] = address.split(',').map((part) => part.trim())

	return (
		<footer className="bg-black-1000 py-16">
			<div className="container max-sm:px-3">
				<div className="flex flex-col gap-8">
					<a href={logo.url} className="inline-flex">
						<figure className="w-14.25 max-w-full">
							<img src={logo.src} alt={logo.alt} className="h-auto w-auto max-w-full" />
						</figure>
					</a>

					<div className="flex flex-col gap-8 lg:flex-row lg:justify-between xl:gap-13">
						<div className="flex flex-col items-start gap-2 lg:shrink-0">
							<div className="text-lg leading-normal text-pure-white">
								{addressLine1 && <p>{addressLine1}</p>}
								{addressLine2 && <p>{addressLine2}</p>}
							</div>

							<div className="flex flex-col items-start">
								{email && <FooterLink text={email} href={`mailto:${email}`} />}
								{phone && <FooterLink text={phone} href={`tel:${phone}`} />}
							</div>
						</div>

						<nav aria-label="Sitemap">
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:flex lg:flex-wrap xl:gap-13">
								{linkColumns.map((column, columnIndex) => (
									<div key={columnIndex}>
										<p className="pb-1 text-lg leading-[1.4] font-semibold whitespace-nowrap text-pure-white">
											{column.heading}
										</p>

										<ul className="flex flex-col gap-0 [&>li]:flex">
											{column.links.map((link, linkIndex) => (
												<li key={linkIndex}>
													<FooterLink text={link.text} href={link.href} isExternal={link.isExternal} />
												</li>
											))}
										</ul>
									</div>
								))}
							</div>
						</nav>
					</div>

					<nav aria-label="Legal">
						<ul className="flex flex-col gap-0 md:flex-row md:flex-wrap md:items-center md:justify-center md:gap-8 [&>li]:flex">
							{legalLinks.map((link, linkIndex) => (
								<li key={linkIndex}>
									<FooterLink text={link.text} href={link.href} isExternal={link.isExternal} />
								</li>
							))}
						</ul>
					</nav>
				</div>
			</div>
		</footer>
	)
}
